import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Base from 'pages/Base/Base';
import FormWrapper from 'components/Form/FormWrapper';
import { addMessage, clearMessages } from 'store/ancillary/actions';
import { UserContext } from 'context/User/UserContext';
import { COMMON_QUESTIONS, FORM_INITIAL_VALUES, FORM_KEYS, FORM_VALIDATION_SCHEMA } from './_const';
import { getChallengeResponsePrepopulation, submitChallengeResponse } from './_api';

/**
 * UI-only challenge-response validation form.
 *
 * @returns {import('react').ReactElement} challenge-response validation page.
 */
const ChallengeResponseValidation = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const [sInitialValues, setInitialValues] = useState(FORM_INITIAL_VALUES);
	const [sProfileInfo, setProfileInfo] = useState(null);
	const [sUnsupportedLegacyQuestion, setUnsupportedLegacyQuestion] = useState('');
	const [sIsLoading, setIsLoading] = useState(true);
	const [sShowSecretAnswer, setShowSecretAnswer] = useState(false);

	const contextUser = useContext(UserContext);
	const user =
		contextUser?.userInfo?.impersonationProfile ??
		contextUser?.userInfo?.userProfile;

	const resolveSupportedQuestionValueHandler = challengeQuestion => {
		const sChallengeQuestion = (challengeQuestion ?? '').trim();

		if (!sChallengeQuestion) {
			return '';
		}

		const sExactValueMatch = COMMON_QUESTIONS.find(
			question =>
				question.value.toLowerCase() === sChallengeQuestion.toLowerCase()
		);

		if (sExactValueMatch) {
			return sExactValueMatch.value;
		}

		return '';
	};

	useEffect(() => {
		let sIsMounted = true;

		const loadPrepopulationHandler = async () => {
			if (!user?.ID) {
				if (sIsMounted) {
					setIsLoading(false);
				}
				return;
			}

			const sPrepopulation = await getChallengeResponsePrepopulation().catch(
				() => null
			);

			if (sIsMounted && sPrepopulation) {
				const sResolvedQuestionValue =
					resolveSupportedQuestionValueHandler(
						sPrepopulation[FORM_KEYS.CHALLENGE_QUESTION]
					);
				const sHasSupportedQuestion = Boolean(sResolvedQuestionValue);
				const sLegacyQuestion =
					typeof sPrepopulation[FORM_KEYS.CHALLENGE_QUESTION] ===
					'string'
						? sPrepopulation[FORM_KEYS.CHALLENGE_QUESTION].trim()
						: '';

				setInitialValues({
					[FORM_KEYS.CHALLENGE_QUESTION]:
						sResolvedQuestionValue ||
						FORM_INITIAL_VALUES[FORM_KEYS.CHALLENGE_QUESTION],
					[FORM_KEYS.SECRET_ANSWER]:
						sHasSupportedQuestion
							? sPrepopulation[FORM_KEYS.SECRET_ANSWER] ??
							  FORM_INITIAL_VALUES[FORM_KEYS.SECRET_ANSWER]
							: FORM_INITIAL_VALUES[FORM_KEYS.SECRET_ANSWER],
					[FORM_KEYS.VALIDATE_WITH_CHALLENGE_RESPONSE]:
						sPrepopulation[
								FORM_KEYS.VALIDATE_WITH_CHALLENGE_RESPONSE
							] ??
						FORM_INITIAL_VALUES[
							FORM_KEYS.VALIDATE_WITH_CHALLENGE_RESPONSE
						],
				});

				setUnsupportedLegacyQuestion(
					sResolvedQuestionValue || !sLegacyQuestion
						? ''
						: sLegacyQuestion
				);
				setProfileInfo(sPrepopulation.profile ?? null);
			}

			if (sIsMounted) {
				setIsLoading(false);
			}
		};

		loadPrepopulationHandler();

		return () => {
			sIsMounted = false;
		};
	}, [user, dispatch]);

	const onSubmitHandler = async (values, actions) => {
		try {
			dispatch(clearMessages());

			await submitChallengeResponse({
				[FORM_KEYS.CHALLENGE_QUESTION]: values[FORM_KEYS.CHALLENGE_QUESTION],
				[FORM_KEYS.SECRET_ANSWER]: values[FORM_KEYS.SECRET_ANSWER],
				[FORM_KEYS.VALIDATE_WITH_CHALLENGE_RESPONSE]:
					values[FORM_KEYS.VALIDATE_WITH_CHALLENGE_RESPONSE],
			});

			setUnsupportedLegacyQuestion('');
			history.push('/ChallengeResponseValidation/Confirmation');
		} catch (error) {
			dispatch(
				addMessage({
					Key: 3,
					Value: error?.message
						? `Error submitting: ${error.message}`
						: 'Unable to submit challenge-response update.',
				})
			);
		} finally {
			actions.setSubmitting(false);
		}
	};

	const toggleShowSecretAnswerHandler = () => {
		setShowSecretAnswer(prev => !prev);
	};

	const isSubmitDisabledHandler = formik => {
		const sRequiresNewSupportedQuestion =
			Boolean(sUnsupportedLegacyQuestion) &&
			!formik.values[FORM_KEYS.CHALLENGE_QUESTION];

		return formik.isSubmitting || sRequiresNewSupportedQuestion || !formik.dirty;
	};

	return (
		<Base title='Challenge Response Validation' loading={sIsLoading}>
			<FormWrapper
				enableReinitialize
				initialValues={sInitialValues}
				validationSchema={FORM_VALIDATION_SCHEMA}
				onSubmit={onSubmitHandler}
			>
				{({ formik }) => (
					<Box sx={{ maxWidth: 780, mx: 'auto', p: 1 }}>
						{sProfileInfo?.fullName && (
							<Typography sx={{ mb: 2 }} color='text.secondary'>
								Editing challenge-response settings for {sProfileInfo.fullName}
								{sProfileInfo.ntid
									? ` (${sProfileInfo.ntid})`
									: ''}
							</Typography>
						)}

						<Box
							sx={{
								mb: 2,
								p: 2,
								borderRadius: 1,
								border: '1px solid',
								borderColor: 'grey.300',
								backgroundColor: 'grey.100',
							}}
						>
							<Typography sx={{ mb: 1 }}>
								Use the form below to update your secret question and answer, which is used to validate your identity in IMS and when contacting the help desk.
							</Typography>
							<Box component='ul' sx={{ m: 0, pl: 3 }}>
								<li>
									<Typography component='span'>
										This question and answer should be unique to UPMC (not used on any other websites).
									</Typography>
								</li>
								<li>
									<Typography component='span'>
										Avoid using information that can be attained from social media.
									</Typography>
								</li>
								<li>
									<Typography component='span'>
										Avoid single-word answers that are dictionary words and apply to a broad user base. Some examples may include favorite color or favorite food.
									</Typography>
								</li>
								<li>
									<Typography component='span'>
										Think of a sentence or phrase that only you would know.
									</Typography>
								</li>
							</Box>
						</Box>

						{sUnsupportedLegacyQuestion && (
							<Box
								sx={{
									mb: 2,
									p: 2,
									borderRadius: 1,
									border: '1px solid',
									borderColor: 'warning.main',
									backgroundColor: 'warning.50',
								}}
							>
								<Typography sx={{ fontWeight: 700, mb: 1 }}>
									Your challenge question is not supported by the new standardized question list.
								</Typography>
								<Typography sx={{ mb: 1 }}>
									Current saved question: <i>{sUnsupportedLegacyQuestion}</i>
								</Typography>
								<Typography sx={{ mb: 1 }}>
									Your saved question/answer will not change until you complete the form below and submit.
								</Typography>
								<Typography>
									It is recommended that you choose a new supported challenge question now.
								</Typography>
							</Box>
						)}

						<Grid container spacing={3}>
							<Grid item xs={12}>
								<TextField
									select
									fullWidth
									name={FORM_KEYS.CHALLENGE_QUESTION}
									label='Challenge Question'
									value={formik.values[FORM_KEYS.CHALLENGE_QUESTION]}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={
										formik.touched[FORM_KEYS.CHALLENGE_QUESTION] &&
										Boolean(
											formik.errors[
												FORM_KEYS.CHALLENGE_QUESTION
											]
										)
									}
									helperText={
										formik.touched[FORM_KEYS.CHALLENGE_QUESTION]
											? formik.errors[
													FORM_KEYS.CHALLENGE_QUESTION
											  ]
											: ''
									}
								>
									<MenuItem value=''>Select a question</MenuItem>
									{COMMON_QUESTIONS.map(sQuestion => (
										<MenuItem
											key={sQuestion.value}
											value={sQuestion.value}
										>
											{sQuestion.label}
										</MenuItem>
									))}
								</TextField>
							</Grid>

							<Grid item xs={12}>
								<TextField
									fullWidth
									type='text'
									name={FORM_KEYS.SECRET_ANSWER}
									label='Secret Answer'
									value={formik.values[FORM_KEYS.SECRET_ANSWER]}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									autoComplete='off'
									sx={{
										'& input': {
											WebkitTextSecurity: sShowSecretAnswer
												? 'none'
												: 'disc',
										},
									}}
									inputProps={{ maxLength: 50 }}
									InputProps={{
										endAdornment: (
											<InputAdornment position='end'>
												<IconButton
													onClick={toggleShowSecretAnswerHandler}
													onMouseDown={event => event.preventDefault()}
													edge='end'
													aria-label={
														sShowSecretAnswer
															? 'Hide secret answer'
															: 'Show secret answer'
													}
												>
													{sShowSecretAnswer ? (
														<VisibilityOff />
													) : (
														<Visibility />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									error={
										formik.touched[FORM_KEYS.SECRET_ANSWER] &&
										Boolean(formik.errors[FORM_KEYS.SECRET_ANSWER])
									}
									helperText={
										formik.touched[FORM_KEYS.SECRET_ANSWER]
											? formik.errors[FORM_KEYS.SECRET_ANSWER]
											: ''
									}
								/>
							</Grid>

							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											name={
												FORM_KEYS.VALIDATE_WITH_CHALLENGE_RESPONSE
											}
											checked={
												formik.values[
													FORM_KEYS.VALIDATE_WITH_CHALLENGE_RESPONSE
												]
											}
											onChange={formik.handleChange}
										/>
									}
									label='Use my challenge question and answer as the primary validation method for this signed-in account.'
								/>
							</Grid>

							<Grid item xs={12} sx={{ textAlign: 'center' }}>
								<Button
									variant='contained'
									type='submit'
									disabled={isSubmitDisabledHandler(formik)}
								>
									{formik.isSubmitting ? 'Submitting...' : 'Submit'}
								</Button>
							</Grid>
						</Grid>
					</Box>
				)}
			</FormWrapper>
		</Base>
	);
};

export default ChallengeResponseValidation;